import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, CheckCircle2, Loader2, Radar } from "lucide-react";

// Mirrors the server-side CHECK on public.leads so we never round-trip a rejection.
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const emailSchema = z
  .string()
  .trim()
  .min(3, "Drop your email to get in.")
  .max(254, "Email is too long.")
  .regex(EMAIL_REGEX, "That doesn't look like a valid email.");

const LeadCapture = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "verifying" | "loading" | "success">("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    const normalized = parsed.data.toLowerCase();

    // Correlation id ties the verifier log row to the lead row for observability.
    const correlationId =
      (globalThis.crypto as Crypto | undefined)?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

    // Server-side deliverability check (disposable + MX lookup)
    setStatus("verifying");
    try {
      const { data: verify, error: verifyErr } = await supabase.functions.invoke(
        "verify-email",
        { body: { email: normalized }, headers: { "x-correlation-id": correlationId } }
      );
      if (verifyErr) throw verifyErr;
      if (verify && verify.valid === false) {
        setStatus("idle");
        setError(verify.reason ?? "We can't deliver to that address.");
        return;
      }
    } catch (_) {
      // Fail-open: don't block submit on verifier outage.
    }

    setStatus("loading");
    const { error: dbError } = await supabase
      .from("leads")
      .insert({
        email: normalized,
        source: "landing_pricing_cta",
        verifier_correlation_id: correlationId,
      });

    if (dbError && dbError.code !== "23505") {
      setStatus("idle");
      // 23514 = check_violation, 23502 = not_null, 22001 = string too long
      if (["23514", "23502", "22001"].includes(dbError.code ?? "")) {
        setError("That doesn't look like a valid email.");
      } else {
        setError("Something glitched on our end. Try again in a moment.");
      }
      return;
    }

    setStatus("success");
  };

  return (
    <section className="pt-24 pb-4">
      <div className="container">
        <div className="relative max-w-3xl mx-auto rounded-2xl border border-border bg-card/60 backdrop-blur p-8 md:p-10 overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 font-mono text-xs text-primary mb-4">
              <Radar className="w-3.5 h-3.5 animate-pulse-dot" />
              <span>// EARLY ACCESS · LIMITED SEATS</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              Get tomorrow's trends in your inbox before the rest of the market wakes up.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Drop your email and we'll send the next 3 rising niches from tonight's scan — free, no card.
            </p>

            {status === "success" ? (
              <div className="mt-6 flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/10 p-4">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-foreground">You're on the list.</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Check your inbox — your first trend drop arrives within 24h.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row gap-3" noValidate>
                <div className="flex-1">
                  <label htmlFor="lead-email" className="sr-only">Email address</label>
                  <Input
                    id="lead-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="operator@yourdomain.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                    disabled={status === "loading" || status === "verifying"}
                    aria-invalid={!!error}
                    aria-describedby={error ? "lead-email-error" : undefined}
                    className="h-12 bg-background/80"
                  />
                  {error && (
                    <p id="lead-email-error" className="mt-2 text-sm text-destructive font-mono">
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="hero"
                  size="lg"
                  disabled={status === "loading" || status === "verifying"}
                  className="h-12"
                >
                  {status === "verifying" ? (
                    <>Verifying <Loader2 className="animate-spin" /></>
                  ) : status === "loading" ? (
                    <>Sending <Loader2 className="animate-spin" /></>
                  ) : (
                    <>Send me trends <ArrowRight /></>
                  )}
                </Button>
              </form>
            )}

            <p className="mt-4 text-xs font-mono text-muted-foreground">
              No spam. Unsubscribe in one click. We never sell your data.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCapture;
